import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import categoryService from '../services/categoryService';
import toast from 'react-hot-toast';

/**
 * CategorySelector Component
 * Dropdown for selecting categories with option to create new ones
 */
const CategorySelector = ({ 
  value, 
  onChange, 
  className = '', 
  categories: propCategories,
  currentCategoryId,
  onSelect,
  onCancel,
  isLoading
}) => {
  const [categories, setCategories] = useState(propCategories || []);
  const [loading, setLoading] = useState(isLoading || true);
  const [isCreating, setIsCreating] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', color: '#6366F1' });
  
  // Fetch categories only if not provided as props
  useEffect(() => {
    if (propCategories && propCategories.length > 0) {
      setCategories(propCategories);
      setLoading(false);
      return;
    }
    
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await categoryService.getAllCategories();
        if (response.success) {
          setCategories(response.data);
        } else {
          console.error('Failed to fetch categories:', response.message);
          toast.error('Failed to load categories');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error('Failed to load categories');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, [propCategories]);
  
  // Handle dropdown selection change
  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    
    if (selectedValue === 'create-new') {
      setIsCreating(true);
    } else if (selectedValue === 'cancel') {
      if (onCancel) {
        onCancel();
      }
    } else {
      if (onSelect) {
        onSelect(selectedValue);
      } else if (onChange) {
        const selectedCategory = categories.find(cat => cat._id === selectedValue);
        onChange(selectedCategory);
      }
    }
  };
  
  // Handle creating a new category
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      setLoading(true);
      const response = await categoryService.createCategory(newCategory);
      
      if (response.success) {
        toast.success(`Category "${newCategory.name}" created`);
        
        // Add the new category to the list
        setCategories(prev => [...prev, response.data]);
        
        // Select the new category
        if (onSelect) {
          onSelect(response.data._id);
        } else if (onChange) {
          onChange(response.data);
        }
        
        // Reset the form
        setNewCategory({ name: '', color: '#6366F1' });
        setIsCreating(false);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Failed to create category');
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel category creation
  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewCategory({ name: '', color: '#6366F1' });
  };
  
  if (isCreating) {
    return (
      <div className={`flex flex-col space-y-2 ${className}`}>
        <div className="flex items-center">
          <input
            type="text"
            placeholder="Category name"
            value={newCategory.name}
            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
          <input
            type="color"
            value={newCategory.color}
            onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
            className="ml-2 p-0 w-10 h-10 rounded-md border-gray-300"
          />
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={handleCancelCreate}
            className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateCategory}
            disabled={loading}
            className="px-2 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    );
  }
  
  const selectedValue = currentCategoryId || (value && value._id) || '';
  
  return (
    <div className="flex">
      <select
        value={selectedValue}
        onChange={handleSelectChange}
        disabled={loading}
        className={`w-full rounded-md border-gray-300 bg-white/90 text-gray-700 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 ${className}`}
        style={{ 
          background: 'rgba(255, 255, 255, 0.9)',
          color: '#374151'
        }}
      >
        <option value="" style={{ background: 'white', color: '#374151' }}>Select a category</option>
        
        {categories.map(category => (
          <option 
            key={category._id} 
            value={category._id}
            style={{ 
              color: category.color,
              background: 'white'
            }}
          >
            {category.name}
          </option>
        ))}
        
        <option 
          value="create-new" 
          className="font-medium"
          style={{ 
            color: '#4F46E5',
            background: 'white'
          }}
        >
          + Create new category
        </option>
        
        {onCancel && (
          <option
            value="cancel"
            className="font-medium"
            style={{
              color: '#DC2626',
              background: 'white'
            }}
          >
            Cancel
          </option>
        )}
      </select>
    </div>
  );
};

CategorySelector.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  className: PropTypes.string,
  categories: PropTypes.array,
  currentCategoryId: PropTypes.string,
  onSelect: PropTypes.func,
  onCancel: PropTypes.func,
  isLoading: PropTypes.bool
};

export default CategorySelector; 